import IEventType, { IMaxBookingsPerTimeUnit } from '@/types/IEventType';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface IUseSectionLimitsArgs {
	service: IEventType;
	onChange: (values: Partial<IEventType>) => void;
}

export default function useSectionLimits(args: IUseSectionLimitsArgs) {
	const isFetching = useIsFetching() > 0;
	const isMutating = useIsMutating() > 0;

	const { t } = useTranslation();
	const form = useForm({
		mode: 'onChange',
		defaultValues: {
			afterBuffer: args.service.afterBuffer || undefined,
			availableUnits: args.service.availableUnits || undefined,
			beforeBuffer: args.service.beforeBuffer || undefined,
			maxBookingNotice: args.service.maxBookingNotice || undefined,
			maxBookingsPerTimeUnit: args.service.maxBookingsPerTimeUnit,
			maxConcurrentBookings: args.service.maxConcurrentBookings || undefined,
			maxUnitsPerBooking: args.service.maxUnitsPerBooking || undefined,
			minBookingNotice: args.service.minBookingNotice || undefined,
			unitsLabel: args.service.unitsLabel || (t('Units') as string),
		},
	});

	function handleBlur(e: ChangeEvent<HTMLInputElement>) {
		args.onChange(_clearValues(form.watch()));
	}

	function handleChange(e: ChangeEvent<HTMLSelectElement>) {
		args.onChange({ [e.target.name]: e.target.value });
	}

	return {
		isLoading: isFetching || isMutating,
		form,
		handleChange,
		handleBlur,
		register: form.register,
		t,
	};
}

function _clearValues(raw: Partial<IEventType>): Partial<IEventType> {
	const maxBookingsPerTimeUnit: IMaxBookingsPerTimeUnit = {};

	maxBookingsPerTimeUnit.perDay = Number.isInteger(raw.maxBookingsPerTimeUnit?.perDay)
		? raw.maxBookingsPerTimeUnit?.perDay
		: undefined;
	maxBookingsPerTimeUnit.perHour = Number.isInteger(raw.maxBookingsPerTimeUnit?.perHour)
		? raw.maxBookingsPerTimeUnit?.perHour
		: undefined;
	maxBookingsPerTimeUnit.perWeek = Number.isInteger(raw.maxBookingsPerTimeUnit?.perWeek)
		? raw.maxBookingsPerTimeUnit?.perWeek
		: undefined;
	maxBookingsPerTimeUnit.perMonth = Number.isInteger(raw.maxBookingsPerTimeUnit?.perMonth)
		? raw.maxBookingsPerTimeUnit?.perMonth
		: undefined;

	raw.maxBookingsPerTimeUnit = maxBookingsPerTimeUnit;

	return raw;
}
